/**
 * How a plan/image overlay is painted on the main map.
 *
 * WHY THIS EXISTS: a baked plan smothers the orthophoto when you zoom out.
 * Measured on a real 3.8 km sheet, the drawing's ink covers 4.2% of the map at
 * full resolution and 48.0% six pyramid levels down — and every one of those
 * pixels is fully opaque. Lines do not fade as you zoom out, they THICKEN
 * relative to the ground: a hairline still occupies one pixel, but that pixel
 * now covers 64x more area. The orthophoto underneath, which is the thing the
 * inspector actually needs to look at, disappears behind a solid mass.
 *
 * The mechanism can't be fixed in the raster pipeline. GDAL builds the
 * transparency pyramid as a MASK (binary by construction), and even if it
 * didn't, the tiler re-binarizes on the way out — rio-tiler does
 * `~values[-1].astype("bool")` on read and multiplies the mask by 255 on
 * render, so the PNG's alpha channel is strictly {0, 255} no matter what the
 * COG holds. Softening the bake alone measures ZERO change on screen. Actually
 * curing it means both containers, a re-bake of every plan and a cache purge —
 * and even then only vector tiles make the linework crisp rather than merely
 * fainter.
 *
 * So this fades the layer instead. It does not make a zoomed-out plan cleaner,
 * only less obstructive, and that trade is deliberate: at site-overview zoom
 * you are reading the site, not the drawing.
 */

import type { Overlay } from '../stores/projectStore';

/** Below this zoom the overlay sits at its faintest.
 *  Retuned 2026-08-25 (was 14/17/0.25): on a ~1 km site the whole-site view
 *  sits around z15.5-16 — mid-band under the old numbers, so the fade had
 *  only half-engaged and dense CAD ink still read as a solid mass ("I do not
 *  see it fading", owner). The band now ends where whole-site viewing
 *  starts, so that view sits at the floor. */
const ZOOM_FAR = 15;
/** At and above this zoom the slider value is honoured exactly. */
const ZOOM_NEAR = 18;
/** Fraction of the user's opacity kept at ZOOM_FAR. */
const FADE_FLOOR = 0.15;
/** Matches the value PlansTicket assigns on import; older saved overlays may
 *  predate it, and `undefined * FADE_FLOOR` is NaN — which poisons the whole
 *  expression rather than degrading to something visible. */
const DEFAULT_OPACITY = 0.75;

/**
 * `raster-opacity` for an overlay layer: the user's slider value when zoomed in
 * to work, fading toward FADE_FLOOR of it when zoomed out to the whole site.
 *
 * Returns a plain `0` when hidden rather than an expression — an interpolation
 * around zero would let a hidden overlay reappear at some zooms, and "I
 * unchecked it and it's still there" is a worse bug than anything this fixes.
 *
 * Mapbox GL 3.18 declares `raster-opacity` as
 * `expression: { interpolated: true, parameters: ["zoom"] }`, so the expression
 * form is supported natively; `interpolate` clamps outside its stop range, so
 * beyond ZOOM_NEAR the rendered value is exactly the slider value.
 */
export function overlayRasterOpacity(
  overlay: Pick<Overlay, 'visible' | 'opacity'>,
): number | unknown[] {
  if (!overlay.visible) return 0;
  const opacity = overlay.opacity ?? DEFAULT_OPACITY;
  return [
    'interpolate', ['linear'], ['zoom'],
    ZOOM_FAR, opacity * FADE_FLOOR,
    ZOOM_NEAR, opacity,
  ];
}
